import { Injectable } from '@angular/core';
import { SweetAlertIcon, SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class AlertService {
  constructor(private translateService: TranslateService) {}

  /**
   * Wraps Swal.fire so every SweetAlert2 popup is promoted to the browser's
   * top layer via the HTML Popover API.
   *
   * Required since Angular CDK 21: MatDialog now uses `popover="manual"` +
   * `element.showPopover()` to render overlays in the top layer. The top layer
   * renders above all normal content regardless of z-index, so a SweetAlert
   * opened on top of a MatDialog would otherwise appear behind it.
   *
   * By giving the .swal2-container the same popover treatment, both overlays
   * live in the top layer and stack in DOM order (latest-opened on top).
   */
  private async swalFire(options: SweetAlertOptions): Promise<SweetAlertResult> {
    const Swal = (await import('sweetalert2')).default;
    const userDidOpen = options.didOpen;
    return Swal.fire({
      ...options,
      didOpen: (popup: HTMLElement) => {
        const container = popup.parentElement as HTMLElement | null;
        if (container && typeof (container as any).showPopover === 'function') {
          try {
            container.setAttribute('popover', 'manual');
            (container as any).showPopover();
          } catch {
            // Popover already shown or unsupported — ignore.
          }
        }
        userDidOpen?.(popup);
      },
    });
  }

  async show(message: string | string[]): Promise<SweetAlertResult> {
    return this.swalFire({
      text: this.translateService.instant(message),
      heightAuto: false,
      confirmButtonColor: '#FF6602'
    });
  }

  async showTitle(title: any, message: string | string[]) {
    this.swalFire({
      title: title,
      text: this.translateService.instant(message),
      heightAuto: false
    });
  }

  async showHtml(title: string | string[], message: string | string[]) {
    this.swalFire({
      title: this.translateService.instant(title),
      html: this.translateService.instant(message),
      heightAuto: false
    });
  }

  async showError(title: string, message: string | string[]) {
    this.swalFire({
      title: 'Erreur',
      text: this.translateService.instant(message),
      icon: 'error',
      heightAuto: false
    });
  }

  async askQuestionWithRange(title: any, text: any, inputAttributes: any, inputValue: any, html: any) {
    const Swal = (await import('sweetalert2')).default;

    return new Promise((resolve, reject) => {
      resolve(
        this.swalFire({
          html: html,
          title: title,
          text: text,
          input: 'range',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#117185',
          cancelButtonColor: '#63c3d7',
          inputAttributes: inputAttributes,
          inputValue: inputValue,
          confirmButtonText: this.translateService.instant('Yes'),
          cancelButtonText: this.translateService.instant('No'),
          heightAuto: false,
          inputValidator: (value): string => {
            if (!value) {
              return 'Le champ ne peut être vide';
            }
            return null;
          },
          didOpen: () => {
            const inputRange = Swal.getInput();
            const inputNumber =
              Swal.getHtmlContainer().querySelector('#range-value');

            inputRange.addEventListener('input', () => {
              inputNumber['value'] = inputRange.value;
            });

            inputNumber.addEventListener('change', () => {
              if (
                inputNumber['value'] <= +inputRange.max &&
                inputNumber['value'] >= 0
              )
                inputRange.value = inputNumber['value'];
              else if (inputNumber['value'] > +inputRange.max)
                inputRange.value = inputRange.max;
              inputRange.nextElementSibling.textContent = inputRange.value;
            });
          }
        })
      );
    });
  }

  async askQuestion(
    title: string | string[],
    text: string | string[],
    input?: any,
    icon?: string,
    value?: string | number | File | FileList | Promise<string | number | File | FileList> | { toPromise: () => string | number | File | FileList; },
    confirmButtonText?: string | string[],
    cancelButtonText?: string | string[]
  ) {
    return new Promise((resolve, reject) => {
      resolve(
        this.swalFire({
          title: this.translateService.instant(title),
          text: this.translateService.instant(text),
          input: input, // 'text'or 'number'
          inputValue: value ? value : '',
          icon: (icon ? icon : 'warning') as SweetAlertIcon,
          showCancelButton: true,
          confirmButtonColor: '#117185',
          cancelButtonColor: '#63c3d7',
          confirmButtonText: confirmButtonText
            ? this.translateService.instant(confirmButtonText)
            : this.translateService.instant('Yes'),
          cancelButtonText: cancelButtonText
            ? this.translateService.instant(cancelButtonText)
            : this.translateService.instant('No'),
          heightAuto: false,
          backdrop: false,
          inputValidator: (value: any) => {
            if (!value) {
              return 'The field cannot be empty';
            }
            return null;
          }
        })
      );
    });
  }

  async ShowQuestion(
    title: string,
    text: string,
    cancelButton: string = null,
    confirmButton: string = null,
    iconText: SweetAlertIcon = null,
    html: boolean = null
  ) {
    let icons: SweetAlertIcon = null;
    if (iconText !== null) icons = iconText;
    let cancelText = this.translateService.instant('No');
    let confirmText = this.translateService.instant('Yes');
    if (cancelButton) cancelText = this.translateService.instant(cancelButton);
    if (confirmButton)
      confirmText = this.translateService.instant(confirmButton);
    if (title)
      title = this.translateService.instant(title);
    if (text)
      text = this.translateService.instant(text);
    return new Promise((resolve, reject) => {
      resolve(
        this.swalFire({
          title: title,
          // Plain text by default (no HTML injection); \n renders as a line break
          ...(html ? { html: text } : { text: text }),
          icon: icons,
          showCancelButton: true,
          cancelButtonColor: '#E5646C',
          confirmButtonColor: '#1DA54D',
          confirmButtonText: confirmText,
          cancelButtonText: cancelText,
          customClass: {
            confirmButton: 'swal2Confirm',
            cancelButton: 'swal2cancel',
            htmlContainer: 'whitespace-pre-line'
          },
          buttonsStyling: true,
          reverseButtons: false
        })
      );
    });
  }
}
